// ICSummary.js - Investment Committee Summary Component (Prompt 12)
import React, { useState, useMemo } from 'react';
import { FileText, Download, LayoutGrid, List } from 'lucide-react';
import './ICSummary.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Format currency values
function formatCurrency(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) return null;
  const num = Number(value);
  if (Math.abs(num) >= 1000000) {
    return `$${(num / 1000000).toFixed(decimals)}M`;
  } else if (Math.abs(num) >= 1000) {
    return `$${(num / 1000).toFixed(decimals)}K`;
  }
  return `$${num.toFixed(decimals)}`;
}

// Format percentage
function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return null;
  return `${(value * 100).toFixed(decimals)}%`;
}

// Compute EBITDA at risk from customer concentration (Prompt 5)
function computeEbitdaAtRisk(customerConcentration, ebitda, revenue) {
  if (!customerConcentration?.top_1 || !ebitda || !revenue) return null;
  
  const largestCustomerPct = customerConcentration.top_1 / 100;
  const ebitdaMargin = ebitda / revenue;
  
  // Shock model: losing largest customer
  const revenueAtRisk = revenue * largestCustomerPct;
  const ebitdaAtRiskDollar = revenueAtRisk * ebitdaMargin;
  const ebitdaAtRiskPct = ebitdaAtRiskDollar / ebitda;
  
  return {
    largestCustomerPct: customerConcentration.top_1,
    ebitdaAtRiskDollar,
    ebitdaAtRiskPct
  };
}

// Compute working capital impact (Prompt 4)
function computeWorkingCapitalImpact(dso, benchmarkDsoDays, revenue) {
  if (!dso || !benchmarkDsoDays || !revenue) return null;
  
  const deltaDays = dso - benchmarkDsoDays;
  const dailyRevenue = revenue / 365;
  const impliedCashRelease = deltaDays * dailyRevenue * -1; // Negative = cash trapped, Positive = cash released
  
  return {
    dso,
    benchmarkDsoDays,
    deltaDays,
    impliedCashRelease,
    classification: deltaDays > 0 ? 'cash_trapped' : deltaDays < 0 ? 'cash_release' : 'neutral'
  };
}

// Compute leverage impact (Prompt 11)
function computeLeverageImpact(netDebt, qoeEbitda, proFormaEbitda) {
  if (!netDebt || !qoeEbitda || !proFormaEbitda) return null;
  if (qoeEbitda <= 0 || proFormaEbitda <= 0) return null;
  
  const baseLeverage = netDebt / qoeEbitda;
  const proFormaLeverage = netDebt / proFormaEbitda;
  const deltaTurns = proFormaLeverage - baseLeverage;
  
  let commentary = 'Neutral';
  if (proFormaLeverage > 4.5) {
    commentary = 'High financing risk';
  } else if (deltaTurns <= -0.5) {
    commentary = 'Value accretive';
  } else if (deltaTurns > 0) {
    commentary = 'Risk-enhancing';
  }
  
  return {
    baseLeverage,
    proFormaLeverage,
    deltaTurns,
    commentary
  };
}

// Extract synergy totals from EBITDA bridge adjustments
function extractSynergyTotals(ebitdaBridge) {
  if (!ebitdaBridge?.adjustments?.synergies) {
    return { grossSynergy: null, riskAdjustedSynergy: null, integrationCost: null };
  }
  
  const synergies = ebitdaBridge.adjustments.synergies;
  const grossSynergy = synergies.reduce((sum, adj) => sum + (adj.amount || 0), 0);
  
  // For now, use gross as risk-adjusted (would come from synergy engine in full implementation)
  const riskAdjustedSynergy = grossSynergy * 0.7; // Assume 70% capture rate
  
  return { grossSynergy, riskAdjustedSynergy, integrationCost: null };
}

function ICSummary({ data, dealName }) {
  const [viewMode, setViewMode] = useState('board'); // 'board' or 'detailed'
  const [downloading, setDownloading] = useState(false);
  
  // Compute derived metrics
  const metrics = useMemo(() => {
    // Extract IC summary data inside useMemo to avoid dependency issues
    const icData = data?.icSummary || {};
    const fin = data?.financial || {};
    const revenue = fin.revenue;
    const ebitda = fin.ebitda;
    const qoeEbitda = icData.qoeEbitda || ebitda;
    
    // EBITDA Margin (Base) = Base EBITDA / Revenue
    const ebitdaMargin = (qoeEbitda && revenue) ? qoeEbitda / revenue : null;
    
    // EBITDA at Risk (Prompt 5)
    const ebitdaAtRisk = computeEbitdaAtRisk(icData.customerConcentration, qoeEbitda, revenue);
    
    // Working Capital Impact (Prompt 4)
    const wcImpact = computeWorkingCapitalImpact(
      icData.workingCapital?.dso,
      icData.workingCapital?.benchmarkDsoDays,
      revenue
    );
    
    // Synergy Totals
    const { grossSynergy, riskAdjustedSynergy } = extractSynergyTotals(icData.ebitdaBridge);
    
    // Integration Cost (would come from synergy engine)
    const integrationCost = icData.synergyAnalysis?.integrationCostY1 || null;
    
    // Pro Forma EBITDA (QoE + Risk-adjusted synergies)
    const proFormaEbitda = qoeEbitda && riskAdjustedSynergy ? qoeEbitda + riskAdjustedSynergy : null;
    
    // Net Stabilized EBITDA Uplift
    const netUplift = riskAdjustedSynergy && integrationCost ? riskAdjustedSynergy - integrationCost : riskAdjustedSynergy;
    
    // Leverage Impact (Prompt 11)
    const leverage = computeLeverageImpact(icData.netDebt, qoeEbitda, proFormaEbitda);
    
    // EBITDA Bridge headline numbers
    const bridgeSteps = icData.ebitdaBridge?.bridge || [];
    const reportedEbitda = bridgeSteps.find(s => s.step === 'Reported EBITDA')?.value;
    const qoeAdjustedEbitda = bridgeSteps.find(s => s.step === 'QoE Adjusted EBITDA')?.value;
    const proFormaStabilized = bridgeSteps.find(s => s.step === 'Pro Forma Stabilized EBITDA')?.value;
    
    // Structural adjustments (QoE corrections)
    const qoeAdjustments = icData.ebitdaBridge?.adjustments?.qoe || [];
    const structuralAdjustments = qoeAdjustments.filter(adj => 
      adj.classification === 'Recurring cost disguised as addback' ||
      adj.classification === 'Structural recurring expense'
    );
    
    return {
      ebitdaAtRisk,
      wcImpact,
      grossSynergy,
      riskAdjustedSynergy,
      integrationCost,
      proFormaEbitda,
      netUplift,
      leverage,
      ebitdaMargin,
      bridge: {
        reportedEbitda,
        qoeAdjustedEbitda: qoeAdjustedEbitda || qoeEbitda,
        proFormaStabilized
      },
      structuralAdjustments
    };
  }, [data]);
  
  // Handle CSV download (editable in Excel)
  const handleDownload = async () => {
    setDownloading(true);
    console.log('[IC Memo CSV Download] Starting download, data:', { 
      dealName, 
      hasData: !!data, 
      hasComputedMetrics: !!metrics 
    });
    try {
      // Sanitize data to only send serializable fields
      // Include all due diligence data: risks, clauses, operational, financial
      const sanitizedData = {
        icSummary: data?.icSummary || {},
        financial: data?.financial || {},
        overall: data?.overall || {},
        executiveSummary: data?.executiveSummary || '',
        counts: data?.counts || {},
        computedMetrics: metrics,
        // Risk Overview data - all risks from all buckets
        items: data?.items || [],
        // Clause Detection data
        detectedClauses: data?.detectedClauses || [],
        // Operational Data
        operational: data?.operational || {}
      };
      
      const requestBody = {
        dealName: dealName || 'Target Company',
        data: sanitizedData,
        generatedAt: new Date().toISOString()
      };
      
      console.log('[IC Memo CSV Download] Request body size:', JSON.stringify(requestBody).length, 'chars');
      
      // Use CSV endpoint for editable format
      const response = await fetch(`${API_URL}/api/ic-memo/csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[IC Memo CSV Download] Server error:', response.status, errorText);
        throw new Error(`Failed to generate CSV: ${response.status} ${errorText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `IC_Memo_${dealName || 'Target'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to generate IC Memo CSV. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  
  // Render Board Summary View
  const renderBoardSummary = () => (
    <div className="ic-board-summary">
      <div className="ic-metrics-grid">
        <div className="ic-metric-card">
          <span className="ic-metric-label">QoE EBITDA (Base)</span>
          <span className="ic-metric-value">{formatCurrency(metrics.bridge.qoeAdjustedEbitda) || 'Not available'}</span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">EBITDA Margin (Base)</span>
          <span className="ic-metric-value">
            {metrics.ebitdaMargin ? (
              <>
                {formatPercent(metrics.ebitdaMargin)}
                <span className="ic-metric-sub">(Calculated)</span>
              </>
            ) : 'Not available'}
          </span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">EBITDA at Risk</span>
          <span className="ic-metric-value">
            {metrics.ebitdaAtRisk ? (
              <>
                {formatCurrency(metrics.ebitdaAtRisk.ebitdaAtRiskDollar)}
                <span className="ic-metric-sub">({formatPercent(metrics.ebitdaAtRisk.ebitdaAtRiskPct)})</span>
              </>
            ) : 'Not available'}
          </span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">Risk-Adjusted Synergy</span>
          <span className="ic-metric-value">{formatCurrency(metrics.riskAdjustedSynergy) || 'Not available'}</span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">Integration Cost (Y1)</span>
          <span className="ic-metric-value">{formatCurrency(metrics.integrationCost) || 'Not available'}</span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">Net Stabilized Uplift</span>
          <span className={`ic-metric-value ${metrics.netUplift >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(metrics.netUplift) || 'Not available'}
          </span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">Working Capital Impact</span>
          <span className={`ic-metric-value ${metrics.wcImpact?.impliedCashRelease > 0 ? 'positive' : metrics.wcImpact?.impliedCashRelease < 0 ? 'negative' : ''}`}>
            {metrics.wcImpact ? (
              <>
                {formatCurrency(metrics.wcImpact.impliedCashRelease)}
                <span className="ic-metric-sub">(one-time cash)</span>
              </>
            ) : 'Not available'}
          </span>
        </div>
        
        <div className="ic-metric-card">
          <span className="ic-metric-label">Leverage Change</span>
          <span className="ic-metric-value">
            {metrics.leverage ? (
              <>
                {metrics.leverage.baseLeverage.toFixed(1)}x → {metrics.leverage.proFormaLeverage.toFixed(1)}x
                <span className={`ic-metric-sub ${metrics.leverage.deltaTurns < 0 ? 'positive' : metrics.leverage.deltaTurns > 0 ? 'negative' : ''}`}>
                  ({metrics.leverage.deltaTurns > 0 ? '+' : ''}{metrics.leverage.deltaTurns.toFixed(1)}x)
                </span>
              </>
            ) : 'Not available'}
          </span>
        </div>
      </div>
      
      {/* Key Deal Posture */}
      <div className="ic-posture-section">
        <h4>Deal Posture</h4>
        <div className="ic-posture-content">
          <div className="ic-posture-item">
            <span className={`ic-posture-indicator ${data?.overall?.score > 60 ? 'negative' : data?.overall?.score > 40 ? 'neutral' : 'positive'}`} />
            <span>
              {data?.overall?.score > 60 
                ? 'Proceed with significant structural protection including price adjustment, escrow, and comprehensive confirmatory diligence'
                : data?.overall?.score > 40
                ? 'Proceed with structured terms and targeted price protection mechanisms'
                : 'Proceed under standard protections with customary due diligence'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Key Gating Items */}
      <div className="ic-gating-section">
        <h4>Key Gating Items</h4>
        <ul className="ic-gating-list">
          {metrics.ebitdaAtRisk?.largestCustomerPct > 30 && (
            <li>Customer concentration: Top customer represents {metrics.ebitdaAtRisk.largestCustomerPct}% of revenue</li>
          )}
          {metrics.structuralAdjustments.length > 0 && (
            <li>EBITDA quality: {metrics.structuralAdjustments.length} structural adjustments identified</li>
          )}
          {metrics.leverage?.proFormaLeverage > 4.5 && (
            <li>Leverage: Pro forma leverage of {metrics.leverage.proFormaLeverage.toFixed(1)}x exceeds 4.5x threshold</li>
          )}
          {data?.counts?.red > 0 && (
            <li>Material risks: {data.counts.red} critical risks require mitigation</li>
          )}
          {data?.counts?.red === 0 && metrics.ebitdaAtRisk?.largestCustomerPct <= 30 && (
            <li>Standard confirmatory diligence</li>
          )}
        </ul>
      </div>
    </div>
  );
  
  // Render Detailed View
  const renderDetailedView = () => (
    <div className="ic-detailed-view">
      {/* EBITDA Bridge Section */}
      <div className="ic-section">
        <h4 className="ic-section-title">EBITDA Bridge</h4>
        <div className="ic-section-content">
          <div className="ic-bridge-row">
            <span>Reported EBITDA</span>
            <span className="ic-bridge-value">{formatCurrency(metrics.bridge.reportedEbitda) || 'Not available'}</span>
          </div>
          <div className="ic-bridge-row">
            <span>QoE Adjusted EBITDA</span>
            <span className="ic-bridge-value">{formatCurrency(metrics.bridge.qoeAdjustedEbitda) || 'Not available'}</span>
          </div>
          {metrics.structuralAdjustments.length > 0 && (
            <div className="ic-bridge-subsection">
              <span className="ic-bridge-subtitle">Structural Adjustments (Top {Math.min(3, metrics.structuralAdjustments.length)}):</span>
              {metrics.structuralAdjustments.slice(0, 3).map((adj, idx) => (
                <div key={idx} className="ic-adjustment-item">
                  <span>{adj.label}</span>
                  <span className={`ic-adjustment-amount ${adj.amount < 0 ? 'negative' : 'positive'}`}>
                    {adj.amount < 0 ? '' : '+'}{formatCurrency(adj.amount)}
                  </span>
                  <span className="ic-adjustment-class">{adj.classification}</span>
                </div>
              ))}
            </div>
          )}
          <div className="ic-bridge-row total">
            <span>Pro Forma Stabilized EBITDA</span>
            <span className="ic-bridge-value">{formatCurrency(metrics.bridge.proFormaStabilized) || formatCurrency(metrics.proFormaEbitda) || 'Not available'}</span>
          </div>
        </div>
      </div>
      
      {/* EBITDA at Risk Section */}
      <div className="ic-section">
        <h4 className="ic-section-title">EBITDA at Risk</h4>
        <div className="ic-section-content">
          {metrics.ebitdaAtRisk ? (
            <>
              <div className="ic-risk-row">
                <span>Largest customer concentration</span>
                <span className="ic-risk-value">{metrics.ebitdaAtRisk.largestCustomerPct.toFixed(1)}%</span>
              </div>
              <div className="ic-risk-row">
                <span>EBITDA at risk (shock scenario)</span>
                <span className="ic-risk-value negative">{formatCurrency(metrics.ebitdaAtRisk.ebitdaAtRiskDollar)}</span>
              </div>
              <div className="ic-risk-row">
                <span>% of EBITDA</span>
                <span className="ic-risk-value negative">{formatPercent(metrics.ebitdaAtRisk.ebitdaAtRiskPct)}</span>
              </div>
            </>
          ) : (
            <span className="ic-not-available">Customer concentration data not available</span>
          )}
        </div>
      </div>
      
      {/* Synergies Section */}
      <div className="ic-section">
        <h4 className="ic-section-title">Synergies</h4>
        <div className="ic-section-content">
          <div className="ic-synergy-row">
            <span>Gross synergy opportunity</span>
            <span className="ic-synergy-value">{formatCurrency(metrics.grossSynergy) || 'Not available'}</span>
          </div>
          <div className="ic-synergy-row">
            <span>Risk-adjusted synergy (70% capture)</span>
            <span className="ic-synergy-value positive">{formatCurrency(metrics.riskAdjustedSynergy) || 'Not available'}</span>
          </div>
          <div className="ic-synergy-row">
            <span>Integration cost (Year 1)</span>
            <span className="ic-synergy-value negative">{formatCurrency(metrics.integrationCost) || 'Not available'}</span>
          </div>
          <div className="ic-synergy-row total">
            <span>Net synergy contribution</span>
            <span className={`ic-synergy-value ${metrics.netUplift >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(metrics.netUplift) || 'Not available'}
            </span>
          </div>
        </div>
      </div>
      
      {/* Working Capital Section */}
      <div className="ic-section">
        <h4 className="ic-section-title">Working Capital</h4>
        <div className="ic-section-content">
          {metrics.wcImpact ? (
            <>
              <div className="ic-wc-row">
                <span>Days Sales Outstanding (DSO)</span>
                <span className="ic-wc-value">{metrics.wcImpact.dso.toFixed(0)} days</span>
              </div>
              <div className="ic-wc-row">
                <span>Benchmark DSO</span>
                <span className="ic-wc-value">{metrics.wcImpact.benchmarkDsoDays} days</span>
              </div>
              <div className="ic-wc-row">
                <span>Variance</span>
                <span className={`ic-wc-value ${metrics.wcImpact.deltaDays > 0 ? 'negative' : metrics.wcImpact.deltaDays < 0 ? 'positive' : ''}`}>
                  {metrics.wcImpact.deltaDays > 0 ? '+' : ''}{metrics.wcImpact.deltaDays.toFixed(0)} days
                </span>
              </div>
              <div className="ic-wc-row total">
                <span>Implied cash impact (one-time)</span>
                <span className={`ic-wc-value ${metrics.wcImpact.impliedCashRelease > 0 ? 'positive' : metrics.wcImpact.impliedCashRelease < 0 ? 'negative' : ''}`}>
                  {formatCurrency(metrics.wcImpact.impliedCashRelease)}
                </span>
              </div>
            </>
          ) : (
            <span className="ic-not-available">Working capital data not available</span>
          )}
        </div>
      </div>
      
      {/* Leverage Section */}
      <div className="ic-section">
        <h4 className="ic-section-title">Leverage</h4>
        <div className="ic-section-content">
          {metrics.leverage ? (
            <>
              <div className="ic-leverage-row">
                <span>Base leverage (Net Debt / QoE EBITDA)</span>
                <span className="ic-leverage-value">{metrics.leverage.baseLeverage.toFixed(1)}x</span>
              </div>
              <div className="ic-leverage-row">
                <span>Pro forma leverage</span>
                <span className="ic-leverage-value">{metrics.leverage.proFormaLeverage.toFixed(1)}x</span>
              </div>
              <div className="ic-leverage-row">
                <span>Change in leverage</span>
                <span className={`ic-leverage-value ${metrics.leverage.deltaTurns < 0 ? 'positive' : metrics.leverage.deltaTurns > 0 ? 'negative' : ''}`}>
                  {metrics.leverage.deltaTurns > 0 ? '+' : ''}{metrics.leverage.deltaTurns.toFixed(1)}x
                </span>
              </div>
              <div className="ic-leverage-commentary">
                <span className={`ic-leverage-chip ${metrics.leverage.commentary === 'High financing risk' ? 'high-risk' : metrics.leverage.commentary === 'Value accretive' ? 'positive' : metrics.leverage.commentary === 'Risk-enhancing' ? 'negative' : 'neutral'}`}>
                  {metrics.leverage.commentary}
                </span>
              </div>
            </>
          ) : (
            <span className="ic-not-available">Leverage data not available (Net Debt or EBITDA missing)</span>
          )}
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="ic-summary-container">
      {/* Header with Toggle and Download */}
      <div className="ic-summary-header">
        <div className="ic-summary-title">
          <FileText size={18} />
          <span>Investment Committee Summary</span>
        </div>
        
        <div className="ic-summary-controls">
          {/* View Toggle */}
          <div className="ic-view-toggle">
            <button 
              className={viewMode === 'board' ? 'active' : ''}
              onClick={() => setViewMode('board')}
              title="Board Summary View"
            >
              <LayoutGrid size={14} />
              Board Summary
            </button>
            <button 
              className={viewMode === 'detailed' ? 'active' : ''}
              onClick={() => setViewMode('detailed')}
              title="Detailed View"
            >
              <List size={14} />
              Detailed
            </button>
          </div>
          
          {/* Download Button */}
          <button 
            className="ic-download-btn"
            onClick={handleDownload}
            disabled={downloading}
            title="Download IC Memo (Excel/CSV)"
          >
            <Download size={14} />
            {downloading ? 'Generating...' : 'Download IC Memo (CSV)'}
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="ic-summary-content">
        {viewMode === 'board' ? renderBoardSummary() : renderDetailedView()}
      </div>
    </div>
  );
}

export default ICSummary;
